import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import T from 'prop-types';
import moment from 'moment';
import { get, isEmpty } from 'lodash';
import { DATE_FORMAT, DATETIME_FORMAT } from 'pages/report/utils/constants';
import { ROW_TYPES } from 'workflow/constants/newInbox';
import Description from '../Description/Description';

import styles from './DetailRows.scss';

const formatDate = (date, format, timezone) => {
    if (timezone) {
        return moment.utc(date).utcOffset(timezone).format(format);
    } else {
        return moment.utc(date).format(format);
    }
};

// Deprecated, use either DateTime row or DateNoTzRow
const DateRow = ({ label, value, timezone }) => {
    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            {value && <div className={styles.TaskDetailRow__Content}>{formatDate(value, DATE_FORMAT, timezone)}</div>}
        </div>
    );
};

// Displays a date with a time used for showing actions at a specific time and in a specific timezone
const DateTimeRow = ({ label, value, timezone }) => {
    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            {value && <div className={styles.TaskDetailRow__Content}>{formatDate(value, DATETIME_FORMAT, timezone)}</div>}
        </div>
    );
};

// Displays a date as a recorded date for a log, no timezone is applied to the date and it is shown in the standard date format
const DateNoTzRow = ({ label, value }) => {
    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            {value && <div className={styles.TaskDetailRow__Content}>{formatDate(value, DATE_FORMAT)}</div>}
        </div>
    );
};

const TextRow = ({ label, value }) => {
    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            {value && <div className={styles.TaskDetailRow__Content}>{value}</div>}
        </div>
    );
};

const RichTextRow = ({ label, value = '{}' }) => {
    const content = JSON.parse(value);
    const hasContent = (content && content.blocks) ? !!get(content, 'blocks[0].text', '') : false;
    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            {hasContent && <div className={styles.TaskDetailRow__Content}>
                <Description content={content} />
            </div>}
        </div>
    );
};

const ListRow = ({ label, value = [], childField }) => {
    const valueRender = () => {
        if (!isEmpty(value) && childField) {
            return value.map(child => get(child, childField, ''));
        }
        if (!childField) {
            return [`(${value.length})`];
        }
        return [''];
    };

    return (
        <div className={styles.TaskDetailRow}>
            <div className={styles.TaskDetailRow__Label}>{label}</div>
            <div className={styles.TaskDetailRow__Content}>
                {valueRender().map((data, idx) => data && <div key={label + idx} className={styles.TaskDetailRow__Content}>{data}</div>)}
            </div>
        </div>
    );
};

const DetailRows = ({ data, configs, timezone, taskType }) => {
    return (
        <Fragment>
            {configs.map(config => {
                const { label, type, field, ...rest } = config;
                const value = config.value || get(data, field);
                switch (type) {
                    case ROW_TYPES.DATE: {
                        return <DateRow key={field + label} label={label} value={value} timezone={timezone} />;
                    }
                    case ROW_TYPES.DATETIME: {
                        return <DateTimeRow key={field + label} label={label} time={true} value={value} timezone={timezone} />;
                    }
                    case ROW_TYPES.DATE_NO_TZ: {
                        return <DateNoTzRow key={field + label} label={label} value={value} />;
                    }
                    case ROW_TYPES.RICH_TEXT: {
                        return <RichTextRow key={field} label={label} value={value} />;
                    }
                    case ROW_TYPES.LIST: {
                        return <ListRow key={field} label={label} value={value} {...rest} />;
                    }
                    default: {
                        return <TextRow key={field + label} label={label} value={value} />;
                    }
                }
            })}
        </Fragment>
    );
};

const RowShape = {
    label: T.string,
    value: T.oneOfType([
        T.string,
        T.array
    ]),
};

DateRow.propTypes = { ...RowShape };

TextRow.propTypes = { ...RowShape };

RichTextRow.propTypes = { ...RowShape };

ListRow.propTypes = { ...RowShape };

DetailRows.propTypes = {
    data: T.object,
    configs: T.arrayOf(T.shape({
        label: T.string,
        field: T.string,
        type: T.oneOf(Object.values(ROW_TYPES))
    }))
};

export { DateRow, DateNoTzRow, TextRow, RichTextRow, ListRow };

export default connect((state, props) => {
    const institutionTimezone = props.timezone || get(state, 'me.institutionTimezone', 'Z');

    return {
        timezone: institutionTimezone,
    };
})(DetailRows);
